import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function errToString(d) {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pwd);
      toast.success("Welcome back.");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen bg-bone flex items-stretch"
    >
      {/* Left — editorial panel */}
      <aside className="hidden lg:flex lg:w-1/2 bg-ink text-white p-16 flex-col justify-between">
        <Link to="/" className="flex items-baseline gap-2">
          <span className="font-serif text-[22px]">ThinkRight</span>
          <span className="text-vermilion text-[22px] font-serif">.</span>
          <span className="overline text-white/60 ml-1">Tech</span>
        </Link>
        <div>
          <div className="overline text-vermilion mb-6">Applicant Tracking</div>
          <h1 className="font-serif text-5xl leading-[1.02] tracking-tight max-w-md">
            Every applicant.
            <br />
            <span className="italic text-white/60">One calm, honest view.</span>
          </h1>
          <p className="mt-8 text-white/60 leading-relaxed max-w-sm">
            A small, focused dashboard to triage, track and close the talent
            pipeline without ceremony.
          </p>
        </div>
        <Link
          to="/"
          className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2 link-underline w-fit"
        >
          Back to site <ArrowUpRight className="w-4 h-4" />
        </Link>
      </aside>

      {/* Right — form */}
      <main className="flex-1 flex items-center justify-center p-8 md:p-16">
        <form
          data-testid="admin-login-form"
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-10"
        >
          <div>
            <div className="flex items-center gap-3 overline text-vermilion mb-4">
              <Lock className="w-3.5 h-3.5" /> Admin access
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Sign in to the ATS.
            </h2>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline text-ink/60">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="admin-login-email"
                className="bg-transparent border-0 border-b border-ink/30 rounded-none h-11 px-0 shadow-none focus-visible:ring-0 focus-visible:border-ink"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="overline text-ink/60">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                data-testid="admin-login-password"
                className="bg-transparent border-0 border-b border-ink/30 rounded-none h-11 px-0 shadow-none focus-visible:ring-0 focus-visible:border-ink"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-primary w-full disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </form>
      </main>
    </div>
  );
}
